import { useEffect, useState } from 'react';
import FusePageSimple from '@fuse/core/FusePageSimple';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { fetchCatalogStats, type CatalogStats } from '@/lib/api';

/**
 * Catalog pipeline health.
 *
 * The quota figure is the one that matters. Amazon's Creators API starts at
 * 8,640 calls a day, and the catalog can only grow as large as a day's quota
 * can keep fresh — so this is a ceiling indicator, not a vanity metric. If the
 * bar is climbing, either the refresh strategy or the catalog size has to give.
 */
function PipelineView() {
	const [stats, setStats] = useState<CatalogStats | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		fetchCatalogStats()
			.then(setStats)
			.catch((e) => setError(e instanceof Error ? e.message : String(e)));
	}, []);

	if (error) {
		return (
			<div className="p-6">
				<Alert severity="error">{error}</Alert>
			</div>
		);
	}

	if (!stats) {
		return (
			<div className="p-6">
				<CircularProgress size={24} />
			</div>
		);
	}

	const { quota, catalog, sources, recentRuns } = stats;
	const quotaColor = quota.percentUsed > 80 ? 'error' : quota.percentUsed > 50 ? 'warning' : 'primary';

	return (
		<FusePageSimple
			header={
				<div className="p-6">
					<Typography variant="h5" className="font-semibold">
						Catalog pipeline
					</Typography>
					<Typography color="text.secondary" variant="body2">
						API consumption, ingestion runs and catalog size.
					</Typography>
				</div>
			}
			content={
				<div className="p-6 flex flex-col gap-5 max-w-4xl">
					<Paper className="p-5">
						<Typography className="font-semibold mb-1">API quota (last 24h)</Typography>
						<Typography variant="body2" color="text.secondary" className="mb-3">
							The daily allowance caps how large a catalog can be kept fresh. It rises
							with sales volume; {quota.allowance.toLocaleString()} is the documented
							starting figure, so treat it as indicative rather than exact.
						</Typography>
						<div className="flex items-baseline gap-2 mb-2">
							<Typography variant="h4">{quota.callsLast24h.toLocaleString()}</Typography>
							<Typography color="text.secondary">
								/ {quota.allowance.toLocaleString()} calls ({quota.percentUsed}%)
							</Typography>
						</div>
						<LinearProgress
							variant="determinate"
							value={Math.min(quota.percentUsed, 100)}
							color={quotaColor}
						/>
						{quota.failedLast24h > 0 && (
							<Alert severity="warning" className="mt-3">
								{quota.failedLast24h} call(s) failed. Failed calls still consume quota.
							</Alert>
						)}
						<div className="mt-4 flex flex-wrap gap-2">
							{Object.entries(quota.byOperation).map(([op, v]) => (
								<Chip
									key={op}
									size="small"
									variant="outlined"
									label={`${op}: ${v.calls} calls · ${v.items} items${v.failed ? ` · ${v.failed} failed` : ''}`}
								/>
							))}
						</div>
					</Paper>

					<Paper className="p-5">
						<Typography className="font-semibold mb-3">Catalog</Typography>
						<div className="flex flex-wrap gap-6">
							<div>
								<Typography variant="h5">{catalog.products.toLocaleString()}</Typography>
								<Typography variant="caption" color="text.secondary">products</Typography>
							</div>
							<div>
								<Typography variant="h5">{catalog.offers.toLocaleString()}</Typography>
								<Typography variant="caption" color="text.secondary">offers</Typography>
							</div>
							<div>
								<Typography variant="h5" color={catalog.pendingMergeReviews > 0 ? 'warning.main' : undefined}>
									{catalog.pendingMergeReviews}
								</Typography>
								<Typography variant="caption" color="text.secondary">awaiting merge review</Typography>
							</div>
						</div>
					</Paper>

					<Paper className="p-5">
						<Typography className="font-semibold mb-3">Sources</Typography>
						{sources.map((s) => (
							<div key={s.id} className="flex flex-wrap items-center gap-2 py-1">
								<Chip size="small" label={s.label} />
								<Typography variant="caption" color="text.secondary">
									{s.kind} · {s.retention}
									{s.offer_ttl_seconds
										? ` · offers expire after ${(s.offer_ttl_seconds / 3600).toFixed(0)}h`
										: ' · no expiry'}
									{s.enabled ? '' : ' · disabled'}
								</Typography>
							</div>
						))}
					</Paper>

					<Paper className="p-5">
						<Typography className="font-semibold mb-3">Recent ingestion runs</Typography>
						{recentRuns.length === 0 && (
							<Typography variant="body2" color="text.secondary">
								No runs recorded yet.
							</Typography>
						)}
						{recentRuns.map((r, i) => (
							<div key={i} className="py-2 border-b last:border-0">
								<div className="flex items-center gap-2">
									<Chip
										size="small"
										label={r.status}
										color={r.status === 'success' ? 'success' : 'error'}
									/>
									<Typography variant="caption" color="text.secondary">
										{new Date(r.started_at).toLocaleString()}
										{r.duration_ms != null ? ` · ${r.duration_ms}ms` : ''}
									</Typography>
								</div>
								{r.details && (
									<Typography variant="caption" color="text.secondary" className="block mt-1">
										{Object.entries(r.details)
											.filter(([k]) => k !== 'sourceId')
											.map(([k, v]) => `${k}: ${String(v)}`)
											.join('  ·  ')}
									</Typography>
								)}
								{r.error_message && (
									<Typography variant="caption" color="error" className="block mt-1">
										{r.error_message}
									</Typography>
								)}
							</div>
						))}
					</Paper>
				</div>
			}
		/>
	);
}

export default PipelineView;
